import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  TextField,
  Autocomplete,
  Divider,
  Card,
  CardHeader,
  CardContent,
  CircularProgress,
  Chip,
  MenuItem,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { DataGrid } from '@mui/x-data-grid';
import { heIL } from '@mui/x-data-grid/locales';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import heLocale from 'date-fns/locale/he';
import { format } from 'date-fns';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../services/api';
import MobileDialog from './dialogs/MobileDialog';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useUser } from '../contexts/UserContext';

const theme = createTheme({
  direction: 'rtl',
  typography: {
    fontSize: 16,
  },
});

const GroupDetailPage = () => {
  const { group_id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { userProfile } = useUser();
  const role = userProfile?.role;
  
  // Get UI configuration from user profile
  const uiConfig = userProfile?.ui_config || {};
  const includeGroups = uiConfig.include_groups !== false; // Default to true if not specified
  const allowedGroupTypes = uiConfig.group_types || ['Flow', 'Year', 'General']; // Default if not specified
  const groupsSchemas = uiConfig.groups_schemas || {};
  const debtFieldsSchema = uiConfig.debt_fields_schema || {};
  const clientFieldsSchema = uiConfig.client_fields_schema || {};
  const ClientAllowedRoles = uiConfig.pages_configs?.ClientDetailPage?.allowed_roles || ["manager", "employee"];

  const [permanentDeleteDialogOpen, setPermanentDeleteDialogOpen] = useState(false);
  const [permanentDeleteStep, setPermanentDeleteStep] = useState(1); // 1: warning, 2: password
  const [permanentDeletePassword, setPermanentDeletePassword] = useState('');
  const [permanentDeleteLoading, setPermanentDeleteLoading] = useState(false);
  const [permanentDeleteError, setPermanentDeleteError] = useState('');
  const [permanentDeleteAnalysis, setPermanentDeleteAnalysis] = useState(null);

  // Add these configuration checks after your existing uiConfig declarations
  const allowPermanentFunctionalGroupDeletion = uiConfig.pages_configs?.GroupDetailPage?.allow_permanent_functional_group_deletion || false;
  const permanentDeleteRoles = uiConfig.pages_configs?.GroupDetailPage?.permanent_delete_functional_group_roles || ['manager'];
  // Helper function to get group type label from schema
  const getGroupTypeLabel = (groupType) => {
    return groupsSchemas[groupType]?.label || 
           (groupType === 'Flow' ? 'שוטף' : 
            groupType === 'Year' ? 'דוח שנתי' : 
            groupType === 'General' ? 'כללי' : 
            groupType);
  };

  // Helper function to get debt field label from schema
  const getDebtFieldLabel = (field) => {
    return debtFieldsSchema[field]?.label || field;
  };

  // ---------- Group info ----------
  const [group, setGroup] = useState(null);

  // ---------- Server-paged clients ----------
  const [clients, setClients] = useState([]); 
  const [loading, setLoading] = useState(false);

  // Pagination & rowCount from server
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [rowCount, setRowCount] = useState(0);

  // Track initial mount to prevent resetting pagination on first render
  const isInitialMount = useRef(true);

  // Search term for filtering main table (server-side)
  const [tableSearchTerm, setTableSearchTerm] = useState('');
  const [debouncedTableSearch, setDebouncedTableSearch] = useState('');

  // ---------- Debounced search for group clients autocomplete ----------
  const [debouncedAutocompleteSearch, setDebouncedAutocompleteSearch] = useState('');
  const autocompleteTimerCleanup = useRef(null);

  const debouncedSetAutocompleteSearch = React.useCallback((value) => {
    const timer = setTimeout(() => {
      setDebouncedAutocompleteSearch(value);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // ---------- Group clients for autocomplete (server-side search, max 100) ----------
  const {
    data: groupClientsAutocomplete = [],
    isLoading: isLoadingGroupClientsAutocomplete,
  } = useQuery({
    queryKey: ['group-clients-autocomplete', group_id, debouncedAutocompleteSearch],
    queryFn: async () => {
      const params = {
        group_id,
        light: 'true',
        limit: 100
      };
      if (debouncedAutocompleteSearch) {
        params.search = debouncedAutocompleteSearch;
      }
      const resp = await API.get('/group/clients/', { params });
      return resp.data.results || [];
    },
    enabled: !!group_id && includeGroups,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // ---------- Autocomplete options with "no results" dummy record ----------
  const autocompleteOptions = useMemo(() => {
    if (groupClientsAutocomplete.length === 0 && debouncedAutocompleteSearch) {
      return [{ ClientBusinessID: -1, ClientName: 'אין תוצאות', isNoResults: true }];
    }
    return groupClientsAutocomplete;
  }, [groupClientsAutocomplete, debouncedAutocompleteSearch]);

  // ---------- "Add Clients" dialog state ----------
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [dialogSelectedClients, setDialogSelectedClients] = useState([]);
  const [selectedAvailableValue, setSelectedAvailableValue] = useState(null);
  const [addClientFilter, setAddClientFilter] = useState('');
  const [isAdding, setIsAdding] = useState(false);

  // ---------- Debounced search for available clients autocomplete ----------
  const [debouncedAvailableAutocompleteSearch, setDebouncedAvailableAutocompleteSearch] = useState('');
  const availableAutocompleteTimerCleanup = useRef(null);

  const debouncedSetAvailableAutocompleteSearch = React.useCallback((value) => {
    const timer = setTimeout(() => {
      setDebouncedAvailableAutocompleteSearch(value);
    }, 300);
    return () => clearTimeout(timer);
  }, []);

  // ---------- Available clients for autocomplete (server-side search, max 100) ----------
  const {
    data: availableClientsAutocomplete = [],
    isLoading: isLoadingAvailableClientsAutocomplete,
  } = useQuery({
    queryKey: ['available-clients-autocomplete', group_id, debouncedAvailableAutocompleteSearch],
    queryFn: async () => {
      const params = {
        group_id,
        light: 'true',
        limit: 100
      };
      if (debouncedAvailableAutocompleteSearch) {
        params.search = debouncedAvailableAutocompleteSearch;
      }
      const resp = await API.get('/clients/available-for-group/', { params });
      return resp.data;
    },
    enabled: !!group_id && includeGroups && addDialogOpen,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // ---------- Available clients for DataGrid (server-side pagination) ----------
  const [availablePaginationModel, setAvailablePaginationModel] = useState({ page: 0, pageSize: 25 });
  const [availableRowCount, setAvailableRowCount] = useState(0);
  const [availableTableSearch, setAvailableTableSearch] = useState('');
  const [debouncedAvailableTableSearch, setDebouncedAvailableTableSearch] = useState('');
  const isInitialAvailableMount = useRef(true);
  const isInitialFilterMount = useRef(true);

  const {
    data: availableClientsData,
    isLoading: isLoadingAvailableClientsPaginated,
  } = useQuery({
    queryKey: ['available-clients-paginated', group_id, availablePaginationModel.page, availablePaginationModel.pageSize, addClientFilter, debouncedAvailableTableSearch],
    queryFn: async () => {
      const params = {
        group_id,
        page: availablePaginationModel.page + 1,
        page_size: availablePaginationModel.pageSize,
      };
      if (addClientFilter) {
        params.filter = addClientFilter;
      }
      if (debouncedAvailableTableSearch) {
        params.search = debouncedAvailableTableSearch;
      }
      const resp = await API.get('/clients/available-for-group/', { params });
      // Return the full response object including count
      return resp.data;
    },
    enabled: !!group_id && includeGroups && addDialogOpen,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  // Extract results and update row count when data changes
  const availableClientsPaginated = availableClientsData?.results || [];

  // Update row count whenever the data changes
  useEffect(() => {
    if (availableClientsData?.count !== undefined) {
      setAvailableRowCount(availableClientsData.count);
    }
  }, [availableClientsData]);

  // ---------- Autocomplete options for available clients with "no results" dummy record ----------
  const availableAutocompleteOptions = useMemo(() => {
    if (availableClientsAutocomplete.length === 0 && debouncedAvailableAutocompleteSearch) {
      return [{ ClientBusinessID: -1, ClientName: 'אין תוצאות', isNoResults: true }];
    }
    return availableClientsAutocomplete;
  }, [availableClientsAutocomplete, debouncedAvailableAutocompleteSearch]);

  // ---------- Track visible client IDs on current page for multi-select ----------
  const visibleAvailableClientIds = useMemo(
    () => new Set((availableClientsPaginated || []).map((client) => client.ClientBusinessID)),
    [availableClientsPaginated]
  );

  // ---------- Autocomplete selected value ----------
  const [selectedSearchValue, setSelectedSearchValue] = useState(null);

  // ---------- Remove client confirm dialog ----------
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedClientForRemoval, setSelectedClientForRemoval] = useState(null);

  // ---------- Edit Group Details dialog ----------
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormStep, setEditFormStep] = useState('form'); // 'form', 'confirm', 'success'
  const [editGroupName, setEditGroupName] = useState('');
  const [editGroupDescription, setEditGroupDescription] = useState('');
  const [editError, setEditError] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const editErrorRef = useRef(null);
  const permanentDeleteErrorRef = useRef(null);

  // Scroll to error when it appears
  useEffect(() => {
    if (editError && editErrorRef.current) {
      editErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [editError]);

  useEffect(() => {
    if (permanentDeleteError && permanentDeleteErrorRef.current) {
      permanentDeleteErrorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [permanentDeleteError]);

  // ---------- Snackbar for success/error ----------
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: '' });

  // ---------- Group Activation History (using normal fetch on every load) ----------
  const [groupHistory, setGroupHistory] = useState([]);
  const [loadingGroupHistory, setLoadingGroupHistory] = useState(false);

  // ---------- Activation Delete dialog ----------
  const [deleteActivationOpen, setDeleteActivationOpen] = useState(false);
  const [deleteActivationStep, setDeleteActivationStep] = useState('confirm'); // 'confirm' | 'password'
  const [deleteActivationTarget, setDeleteActivationTarget] = useState(null);
  const [deleteActivationPassword, setDeleteActivationPassword] = useState('');
  const [deleteActivationPwVisible, setDeleteActivationPwVisible] = useState(false);
  const [deleteActivationLoading, setDeleteActivationLoading] = useState(false);
  const [deleteActivationError, setDeleteActivationError] = useState('');

  // ---------- Activation Edit (date + note) dialog ----------
  const [editActivationOpen, setEditActivationOpen] = useState(false);
  const [editActivationStep, setEditActivationStep] = useState('form'); // 'form' | 'confirm' | 'password'
  const [editActivationTarget, setEditActivationTarget] = useState(null);
  const [editActivationDate, setEditActivationDate] = useState(null);
  const [editActivationNote, setEditActivationNote] = useState('');
  const [editActivationPassword, setEditActivationPassword] = useState('');
  const [editActivationPwVisible, setEditActivationPwVisible] = useState(false);
  const [editActivationLoading, setEditActivationLoading] = useState(false);
  const [editActivationError, setEditActivationError] = useState('');

  // Navigate away if groups functionality is disabled
  useEffect(() => {
    if (!includeGroups) {
      navigate('/');
    }
  }, [includeGroups, navigate]);

  // Debounce table search and reset pagination
  useEffect(() => {
    // Skip reset on initial mount to preserve any cached state
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      // Reset pagination immediately when search changes (not debounced)
      setPaginationModel(prev => ({ ...prev, page: 0 }));
    }

    // Debounce the actual search term update
    const timer = setTimeout(() => {
      setDebouncedTableSearch(tableSearchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [tableSearchTerm]);

  // Debounce available table search and reset pagination
  useEffect(() => {
    // Skip reset on initial mount
    if (isInitialAvailableMount.current) {
      isInitialAvailableMount.current = false;
    } else {
      // Reset pagination immediately when search changes (not debounced)
      setAvailablePaginationModel(prev => ({ ...prev, page: 0 }));
    }

    // Debounce the actual search term update
    const timer = setTimeout(() => {
      setDebouncedAvailableTableSearch(availableTableSearch);
    }, 300);

    return () => clearTimeout(timer);
  }, [availableTableSearch]);

  // Reset pagination when filter changes (but not on initial mount)
  useEffect(() => {
    if (isInitialFilterMount.current) {
      isInitialFilterMount.current = false;
    } else {
      setAvailablePaginationModel(prev => ({ ...prev, page: 0 }));
    }
  }, [addClientFilter]);

  const fetchGroupHistory = React.useCallback(async () => {
    if (!includeGroups || !group_id) return;
    setLoadingGroupHistory(true);
    try {
      const resp = await API.get('/group/history/', { params: { group_id } });
      setGroupHistory(resp.data);
    } catch (error) {
      console.error('Error fetching group history:', error);
      setGroupHistory([]);
    } finally {
      setLoadingGroupHistory(false);
    }
  }, [group_id, includeGroups]);

  useEffect(() => {
    fetchGroupHistory();
  }, [fetchGroupHistory]);

  // Helper to invalidate all client-related caches after a balance-affecting change
  const invalidateClientCaches = React.useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['clients'], refetchType: 'active' });
  }, [queryClient]);

  // Open delete activation dialog
  const openDeleteActivation = (row) => {
    setDeleteActivationTarget(row);
    setDeleteActivationStep('confirm');
    setDeleteActivationPassword('');
    setDeleteActivationPwVisible(false);
    setDeleteActivationError('');
    setDeleteActivationLoading(false);
    setDeleteActivationOpen(true);
  };
  const closeDeleteActivation = () => {
    setDeleteActivationOpen(false);
    setDeleteActivationTarget(null);
  };
  const submitDeleteActivation = async () => {
    if (deleteActivationLoading) return;
    setDeleteActivationLoading(true);
    setDeleteActivationError('');
    try {
      await API.post('/group/action/delete/', {
        history_id: deleteActivationTarget.id,
        password: deleteActivationPassword,
      });
      await invalidateClientCaches();
      await fetchGroupHistory();
      setSnackbar({ open: true, message: 'הפעולה נמחקה בהצלחה', severity: 'success' });
      closeDeleteActivation();
    } catch (error) {
      if (error.response && error.response.status === 401) {
        setDeleteActivationError('סיסמה שגויה');
      } else if (error.response && error.response.status === 409) {
        setDeleteActivationError('רישום ישן ללא תנועות מקושרות — לא ניתן למחוק');
      } else {
        setDeleteActivationError('שגיאה במחיקת הפעולה');
      }
    } finally {
      setDeleteActivationLoading(false);
    }
  };

  // Open edit activation dialog
  const openEditActivation = (row) => {
    setEditActivationTarget(row);
    setEditActivationStep('form');
    // Prefer actual_date if recorded, otherwise default to activated_at
    const initial = row.actual_date || row.activated_at;
    setEditActivationDate(initial ? new Date(initial) : null);
    setEditActivationNote(row.note || '');
    setEditActivationPassword('');
    setEditActivationPwVisible(false);
    setEditActivationError('');
    setEditActivationLoading(false);
    setEditActivationOpen(true);
  };
  const closeEditActivation = () => {
    setEditActivationOpen(false);
    setEditActivationTarget(null);
  };
  const submitEditActivation = async () => {
    if (editActivationLoading) return;
    if (!editActivationDate) {
      setEditActivationError('יש לבחור תאריך');
      return;
    }
    setEditActivationLoading(true);
    setEditActivationError('');
    try {
      const payload = {
        history_id: editActivationTarget.id,
        actual_date: editActivationDate.toISOString(),
        password: editActivationPassword,
      };
      // Opt-in: only send note when the user actually changed it, so unrelated
      // edits don't rewrite the note across every linked transaction.
      const originalNote = editActivationTarget.note || '';
      if (editActivationNote !== originalNote) {
        payload.note = editActivationNote;
      }
      await API.post('/group/action/update-date/', payload);
      await invalidateClientCaches();
      await fetchGroupHistory();
      setSnackbar({ open: true, message: 'תאריך הפעולה עודכן בהצלחה', severity: 'success' });
      closeEditActivation();
    } catch (error) {
      if (error.response && error.response.status === 401) {
        setEditActivationError('סיסמה שגויה');
      } else if (error.response && error.response.status === 409) {
        setEditActivationError('רישום ישן ללא תנועות מקושרות — לא ניתן לעדכן');
      } else {
        setEditActivationError('שגיאה בעדכון התאריך');
      }
    } finally {
      setEditActivationLoading(false);
    }
  };

  // ---------- History DataGrid Columns ----------
  const formatDateTime = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    return date.toLocaleDateString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };
  const formatDateOnly = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    return date.toLocaleDateString('he-IL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const historyColumns = [
    {
      field: 'activated_at',
      headerName: 'תאריך רישום',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => formatDateTime(params.value),
    },
    {
      field: 'actual_date',
      headerName: 'תאריך בפועל',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      // Sort by activated_at when actual_date is missing so legacy rows keep
      // their order — but display the activation timestamp visibly so it's
      // clear nothing was backdated.
      valueGetter: (value, row) => row.actual_date || row.activated_at,
      renderCell: (params) => {
        return params.row.actual_date
          ? formatDateOnly(params.row.actual_date)
          : formatDateTime(params.row.activated_at);
      },
    },
    {
      field: 'activated_by_full_name',
      headerName: 'על ידי',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'amount',
      headerName: 'סכום',
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'target_field',
      headerName: 'שדה יעד',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => getDebtFieldLabel(params.value || ''),
    },
    {
      field: 'note',
      headerName: 'הערות',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'actions',
      headerName: 'פעולות',
      flex: 0.8,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        // Legacy records (created before transactions were linked back to the
        // activation) have transactions_count === 0 and can't be edited or
        // deleted — there are no linked transactions to recalc.
        const linked = params.row.transactions_count ?? 0;
        const disabled = linked === 0;
        const tooltip = disabled
          ? 'רישום ישן ללא תנועות מקושרות — לא ניתן לערוך או למחוק'
          : '';
        return (
          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }} title={tooltip}>
            <IconButton
              color="primary"
              onClick={() => openEditActivation(params.row)}
              aria-label="ערוך תאריך"
              size="small"
              disabled={disabled}
            >
              <EditIcon />
            </IconButton>
            <IconButton
              color="error"
              onClick={() => openDeleteActivation(params.row)}
              aria-label="מחק"
              size="small"
              disabled={disabled}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];

  // =====================================================
  //  1) Fetch group details & server-paged clients
  // =====================================================
  useEffect(() => {
    if (!includeGroups) return;
    
    const fetchGroupDetails = async () => {
      try {
        setLoading(true);
        const groupResponse = await API.get('/group/', { params: { group_id } });
        setGroup(groupResponse.data);
        // Initialize edit form with current values
        setEditGroupName(groupResponse.data.GroupName);
        setEditGroupDescription(groupResponse.data.Description || '');
      } catch (error) {
        console.error('Error fetching group details:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchGroupClientsPaginated = async () => {
      try {
        setLoading(true);
        const params = {
          group_id,
          page: paginationModel.page + 1, // DRF is 1-based
          page_size: paginationModel.pageSize,
        };

        // Add search parameter if present
        if (debouncedTableSearch) {
          params.search = debouncedTableSearch;
        }

        const response = await API.get('/group/clients/', { params });
        setClients(response.data.results || []);
        setRowCount(response.data.count || 0);
      } catch (err) {
        console.error('Error fetching paginated group clients:', err);
      } finally {
        setLoading(false);
      }
    };

    if (group_id) {
      fetchGroupDetails();
      fetchGroupClientsPaginated();
    }
  }, [group_id, paginationModel.page, paginationModel.pageSize, debouncedTableSearch, includeGroups]);

  // =====================================================
  //  2) No client-side filtering - always show paginated data
  // =====================================================
  // Autocomplete is now separate from table - table always shows server-paginated data
  const filteredClients = clients;

  // =====================================================
  //  4) Remove + Add Clients
  // =====================================================
  const confirmRemoveClient = (client) => {
    setSelectedClientForRemoval(client);
    setConfirmDialogOpen(true);
  };

  const handleRemoveClient = async () => {
    if (!selectedClientForRemoval) return;
    try {
      await API.post('/group/remove-clients/', {
        group_id: parseInt(group_id, 10),
        client_business_ids: [selectedClientForRemoval.ClientBusinessID],
      });
      setClients((prev) =>
        prev.filter((c) => c.ClientBusinessID !== selectedClientForRemoval.ClientBusinessID)
      );
      queryClient.setQueryData(['all-group-clients', group_id], (old = []) =>
        old.filter((x) => x.ClientBusinessID !== selectedClientForRemoval.ClientBusinessID)
      );
      setSnackbar({ open: true, message: 'לקוח הוסר בהצלחה!', severity: 'success' });
    } catch (error) {
      console.error('Error removing client:', error);
      setSnackbar({
        open: true,
        message: 'אירעה שגיאה בהסרת לקוח.',
        severity: 'error',
      });
    } finally {
      setConfirmDialogOpen(false);
      setSelectedClientForRemoval(null);
    }
  };

  const handleOpenAddDialog = () => {
    // Clear any previous selections and open immediately
    setDialogSelectedClients([]);
    setAddDialogOpen(true);
    // React Query will automatically refetch if data is stale
  };

  const handleCloseAddDialog = () => {
    // Clear all dialog state when closing
    setAddDialogOpen(false);
    setDialogSelectedClients([]);
    setAddClientFilter('');
    setSelectedAvailableValue(null);
    setDebouncedAvailableAutocompleteSearch('');
    setAvailableTableSearch('');
    setDebouncedAvailableTableSearch('');
    setAvailablePaginationModel({ page: 0, pageSize: 25 });
  };

  const handleAddClients = async () => {
    if (isAdding) return;
    if (dialogSelectedClients.length === 0) {
      setSnackbar({ open: true, message: 'לא נבחרו לקוחות.', severity: 'warning' });
      return;
    }
    setIsAdding(true);
    try {
      await API.post('group/add-clients/', {
        group_id: parseInt(group_id, 10),
        client_business_ids: dialogSelectedClients,
      });
      setSnackbar({ open: true, message: 'לקוחות נוספו בהצלחה!', severity: 'success' });
      handleCloseAddDialog();

      const response = await API.get('/group/clients/', {
        params: {
          group_id,
          page: paginationModel.page + 1, // DRF is 1-based
          page_size: paginationModel.pageSize,
        },
      });
      setClients(response.data.results || []);
      setRowCount(response.data.count || 0);
      // Invalidate both queries to reflect the changes
      queryClient.invalidateQueries(['all-group-clients', group_id]);
      queryClient.invalidateQueries(['available-clients-for-group', group_id]);
    } catch (error) {
      console.error('Error adding clients:', error);
      setSnackbar({
        open: true,
        message: 'אירעה שגיאה בהוספת לקוחות.',
        severity: 'error',
      });
    } finally {
      setIsAdding(false);
    }
  };

  // =====================================================
  //  5) Update Group Details
  // =====================================================
  const handleOpenEditDialog = () => {
    if (group) {
      setEditGroupName(group.GroupName);
      setEditGroupDescription(group.Description || '');
    }
    setEditDialogOpen(true);
    setEditFormStep('form');
    setEditError('');
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditFormStep('form');
    setEditError('');
    // Reset form values to current group values
    if (group) {
      setEditGroupName(group.GroupName);
      setEditGroupDescription(group.Description || '');
    }
  };

  const handleEditFormNextStep = () => {
    // Validate form
    if (!editGroupName.trim()) {
      setEditError('שם הקבוצה הוא שדה חובה');
      return;
    }
    setEditFormStep('confirm');
    setEditError('');
  };

  const handleEditSubmit = async () => {
    try {
      setEditLoading(true);
      setEditError('');
      
      await API.put('/group/update/', {
        group_id: parseInt(group_id, 10),
        group_name: editGroupName,
        description: editGroupDescription,
      });
      
      // Success
      setEditFormStep('success');
      
      // Refresh group data
      const groupResponse = await API.get('/group/', { params: { group_id } });
      setGroup(groupResponse.data);
      
      // Success - no need for additional snackbar as we already show success in dialog
      
      // Close dialog after short delay
      setTimeout(() => {
        handleCloseEditDialog();
      }, 1500);
      
    } catch (error) {
      console.error('Error updating group:', error);
      setEditError(error.response?.data?.error || 'אירעה שגיאה בעדכון פרטי הקבוצה');
    } finally {
      setEditLoading(false);
    }
  };

    // Add these handler functions before your return statement
  const handleOpenPermanentDeleteDialog = async () => {
    setPermanentDeleteDialogOpen(true);
    setPermanentDeleteStep(1);
    setPermanentDeletePassword('');
    setPermanentDeleteError('');
    
    // Get analysis of what will happen
    try {
      const response = await API.post('/group/analyze-permanent-delete/', {
        group_id: parseInt(group_id, 10)
      });
      setPermanentDeleteAnalysis(response.data);
    } catch (error) {
      console.error('Error analyzing permanent delete:', error);
      // Continue anyway - user can still proceed without analysis
    }
  };

  const handleCancelPermanentDelete = () => {
    setPermanentDeleteDialogOpen(false);
    setPermanentDeleteStep(1);
    setPermanentDeletePassword('');
    setPermanentDeleteError('');
    setPermanentDeleteLoading(false);
    setPermanentDeleteAnalysis(null);
  };

  const handleConfirmPermanentDelete = async () => {
    setPermanentDeleteLoading(true);
    setPermanentDeleteError('');
    
    try {
      const response = await API.delete('/group/permanent-delete/', {
        data: {
          group_id: parseInt(group_id, 10),
          password: permanentDeletePassword,
        },
      });
      
      // Success - show detailed results and navigate
      const result = response.data;
      const successMessage = `קבוצה נמחקה בהצלחה! ${result.permanently_deleted_clients} לקוחות נמחקו לצמיתות, ${result.kept_clients} לקוחות נשמרו`;
      
      setSnackbar({ 
        open: true, 
        message: successMessage, 
        severity: 'success' 
      });
      
      setTimeout(() => {
        navigate('/groups');
      }, 3000);
      
    } catch (error) {
      console.error('Error permanently deleting functional group:', error);
      if (error.response && error.response.status === 401) {
        setPermanentDeleteError('סיסמה שגויה');
      } else {
        setPermanentDeleteError(error?.response?.data?.error || 'שגיאה במחיקת הקבוצה');
      }
      setPermanentDeleteLoading(false);
    }
  };
  // =====================================================
  //  6) DataGrid columns for Main Table
  // =====================================================
  const mainTableColumns = [
    {
      field: 'ClientName',
      headerName: clientFieldsSchema?.ClientName?.label || 'שם הלקוח',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const canNavigateToClient = ClientAllowedRoles.includes(role);
        
        if (canNavigateToClient) {
          return (
            <Button 
              variant="text" 
              color="primary" 
              onClick={() => navigate(`/client/${params.row.ClientBusinessID}`)}
              sx={{ textDecoration: 'underline', cursor: 'pointer' }}
            >
              {params.row.ClientName}
            </Button>
          );
        } else {
          return (
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.primary',
                textAlign: 'center',
                fontSize: '1.1rem'
              }}
            >
              {params.row.ClientName}
            </Typography>
          );
        }
      },
    },
    {
      field: 'ClientBusinessID',
      headerName: 'תז/חפ',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'Actions',
      headerName: 'פעולות',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Button color="error" variant="outlined" onClick={() => confirmRemoveClient(params.row)}>
          הסר
        </Button>
      ),
    },
  ];

  // =====================================================
  //  7) DataGrid columns for Add Clients Dialog (including Groups)
  // =====================================================
  const addDialogColumns = [
    {
      field: 'ClientName',
      headerName: clientFieldsSchema?.ClientName?.label || 'שם הלקוח',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const canNavigateToClient = ClientAllowedRoles.includes(role);
        
        if (canNavigateToClient) {
          return (
            <Button
              variant="text"
              color="primary"
              onClick={(e) => {
                e.stopPropagation();
                window.open(`/client/${params.row.ClientBusinessID}`, '_blank');
              }}
              sx={{ textDecoration: 'underline', cursor: 'pointer' }}
            >
              {params.row.ClientName}
            </Button>
          );
        } else {
          return (
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.primary',
                textAlign: 'center',
                fontSize: '1.1rem'
              }}
            >
              {params.row.ClientName}
            </Typography>
          );
        }
      },
    },
    {
      field: 'ClientBusinessID',
      headerName: 'תז/חפ',
      flex: 1,
      align: 'center',
      headerAlign: 'center',
    },
    {
      field: 'groups',
      headerName: 'קבוצות',
      flex: 1,
      renderCell: (params) => {
        const row = params.row;
        const groups = [];
        if (row.FlowGroup) groups.push(row.FlowGroup);
        if (row.YearGroup) groups.push(row.YearGroup);
        if (row.GeneralGroups && row.GeneralGroups.length > 0) {
          groups.push(...row.GeneralGroups);
        }
        
        // Dynamic color scheme based on group type
        const getChipColors = (groupType) => {
          // Use hash of group type to generate a consistent but varied color
          const getHashCode = str => {
            let hash = 0;
            for (let i = 0; i < str.length; i++) {
              hash = ((hash << 5) - hash) + str.charCodeAt(i);
              hash = hash & hash; // Convert to 32bit integer
            }
            return Math.abs(hash);
          };
          
          // Generate colors based on allowed group types
          const colors = {
            Flow: { backgroundColor: '#e3f2fd', color: '#1976d2' },
            Year: { backgroundColor: '#fff3e0', color: '#fb8c00' },
            General: { backgroundColor: '#e8f5e9', color: '#43a047' }
          };
          
          // If we have a predefined color, use it
          if (colors[groupType]) {
            return colors[groupType];
          }
          
          // Otherwise generate one consistently
          const hash = getHashCode(groupType);
          const hue = hash % 360;
          return {
            backgroundColor: `hsl(${hue}, 85%, 95%)`,
            color: `hsl(${hue}, 85%, 35%)`
          };
        };
        
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {groups.length > 0
              ? groups.map((group) => {
                  const colors = getChipColors(group.GroupType);
                  return (
                    <Chip
                      key={group.GroupID}
                      label={group.GroupName}
                      size="small"
                      variant="outlined"
                      sx={{
                        borderRadius: '9999px',
                        fontSize: '0.75rem',
                        padding: '2px 4px',
                        backgroundColor: colors.backgroundColor,
                        color: colors.color,
                        borderColor: colors.color,
                      }}
                    />
                  );
                })
              : 'לא הוגדר'}
          </Box>
        );
      },
    },
  ];

  if (!includeGroups) {
    return null;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={heLocale}>
    <ThemeProvider theme={theme}>
      <div dir="rtl">
        <Container maxWidth={false} sx={{ width: '100%', px: 2 }}>
          <Box sx={{ textAlign: 'right', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <IconButton
                onClick={() => navigate('/groups')}
                size="large"
                sx={{ color: 'primary.main' }}
              >
                <ArrowBackIcon sx={{ fontSize: '2.5rem' }} />
              </IconButton>
              <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: '2.5rem' }}>
                {group?.GroupName}
              </Typography>
            </Box>
            <Typography variant="h6" sx={{ marginBottom: '0.5rem' }}>
              סוג קבוצה: {group ? getGroupTypeLabel(group.GroupType) : ''}
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.2rem' }}>
              תיאור: {group?.Description || 'אין תיאור'}
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.2rem' }}>
              מספר לקוחות: {rowCount}
            </Typography>
            
            <Button 
              variant="contained" 
              color="primary" 
              onClick={handleOpenEditDialog}
              sx={{ mt: 2, fontSize: '1.1rem', ml: 2}}
            >
              ערוך פרטים
            </Button>
            {allowPermanentFunctionalGroupDeletion && permanentDeleteRoles.includes(role) && (
              <Button 
                variant="contained" 
                color="error" 
                onClick={handleOpenPermanentDeleteDialog}
                sx={{ 
                  mt: 2, 
                  ml: 2,
                  fontSize: '1.1rem',
                  backgroundColor: '#d32f2f',
                  '&:hover': { backgroundColor: '#b71c1c' }
                }}
              >
                מחיקה קבועה של הקבוצה
              </Button>
            )}
          </Box>

          <Divider sx={{ my: 4 }} />
          
          <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
            <CardHeader
              title={
                <Typography sx={{ fontWeight: 'bold', fontSize: '1.3rem' }}>
                  לקוחות
                </Typography>
              }
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                py: 1.5,
              }}
            />
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Autocomplete
                  freeSolo
                  options={autocompleteOptions}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    if (option.isNoResults) return option.ClientName;
                    return `${option.ClientName} (${option.ClientBusinessID})`;
                  }}
                  getOptionDisabled={(option) => option.isNoResults === true}
                  value={selectedSearchValue}
                  openOnFocus
                  onInputChange={(event, newValue, reason) => {
                    // Clear previous timer
                    if (autocompleteTimerCleanup.current) {
                      autocompleteTimerCleanup.current();
                    }

                    // Debounce the API call - but DON'T update table search on every keystroke
                    autocompleteTimerCleanup.current = debouncedSetAutocompleteSearch(newValue || '');

                    // Clear table search if input is emptied (manually deleted all text)
                    if (newValue.trim() === '' && tableSearchTerm !== '') {
                      setTableSearchTerm('');
                      setSelectedSearchValue(null);
                    }
                  }}
                  onChange={(event, newValue) => {
                    // Ignore dummy "no results" record
                    if (newValue && typeof newValue === 'object' && newValue.isNoResults) {
                      return;
                    }

                    if (typeof newValue === 'string' && newValue.trim() !== '') {
                      // Typed and pressed enter - NOW update table search
                      setTableSearchTerm(newValue);
                      setSelectedSearchValue(null);
                    } else if (newValue && typeof newValue === 'object') {
                      // Selected an option - NOW update table search by client business ID
                      setTableSearchTerm(newValue.ClientBusinessID);
                      setSelectedSearchValue(newValue);
                    } else {
                      // Cleared or empty string - clear table search and autocomplete search
                      setTableSearchTerm('');
                      setSelectedSearchValue(null);
                      setDebouncedAutocompleteSearch('');
                      // Clear any pending timer
                      if (autocompleteTimerCleanup.current) {
                        autocompleteTimerCleanup.current();
                      }
                    }
                  }}
                  isOptionEqualToValue={(option, value) => option?.ClientBusinessID === value?.ClientBusinessID}
                  filterOptions={(x) => x}
                  loading={isLoadingGroupClientsAutocomplete}
                  loadingText="טוען..."
                  forcePopupIcon={false}
                  renderInput={(params) => (
                    <TextField {...params} label="חיפוש לפי שם או מזהה" placeholder="הקלד שם או תז/חפ" size="small" sx={{ width: { xs: '100%', md: 300 }, minWidth: { xs: 0, md: 300 } }} />
                  )}
                />
                <Button variant="contained" color="primary" onClick={handleOpenAddDialog}>
                  הוסף לקוחות לקבוצה
                </Button>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={() => navigate(`/group/${group_id}/minimal-print`)}
                >
                  הדפס
                </Button>
              </Box>
              <Box sx={{ width: '100%' }}>
                <DataGrid
                  rows={filteredClients}
                  columns={mainTableColumns}
                  getRowId={(row) => row.ClientBusinessID}
                  loading={loading}
                  paginationMode="server"
                  rowCount={rowCount}
                  pageSizeOptions={[25, 50, 100]}
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  disableSelectionOnClick
                  localeText={heIL.components.MuiDataGrid.defaultProps.localeText}
                  autoHeight
                  sx={{
                    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 'bold', fontSize: '1.2rem' },
                    '.MuiDataGrid-cell': { fontSize: '1.1rem' },
                  }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Edit Group Details Dialog */}
          <MobileDialog open={editDialogOpen} onClose={handleCloseEditDialog} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ fontSize: '1.8rem' }}>
              {editFormStep === 'form' 
                ? 'עריכת פרטי קבוצה' 
                : editFormStep === 'confirm' 
                ? 'אישור עדכון' 
                : 'עדכון הושלם'}
            </DialogTitle>
            <DialogContent>
              {editError && (
                <Alert ref={editErrorRef} severity="error" sx={{ mb: 2, mt: 1 }}>
                  {editError}
                </Alert>
              )}
              
              {editFormStep === 'form' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                  <TextField
                    label="שם הקבוצה"
                    value={editGroupName}
                    onChange={(e) => setEditGroupName(e.target.value)}
                    fullWidth
                    required
                  />
                  <TextField
                    label="תיאור"
                    value={editGroupDescription}
                    onChange={(e) => setEditGroupDescription(e.target.value)}
                    multiline
                    minRows={3}
                    fullWidth
                  />
                </Box>
              )}
              
              {editFormStep === 'confirm' && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body1" sx={{ mb: 2, fontSize: '1.2rem' }}>
                    האם לעדכן את פרטי הקבוצה לפי הפרטים הבאים?
                  </Typography>
                  <Typography sx={{ fontSize: '1.2rem' }}>
                    <span style={{ fontWeight: 'bold' }}>שם קבוצה:</span> <span style={{ fontWeight: 'bold' }}>{editGroupName}</span>
                  </Typography>
                  <Typography sx={{ fontSize: '1.2rem', mt: 1 }}>
                    <span style={{ fontWeight: 'bold' }}>תיאור:</span> <span style={{ fontWeight: 'bold' }}>{editGroupDescription || 'אין תיאור'}</span>
                  </Typography>
                </Box>
              )}
              
              {editFormStep === 'success' && (
                <Box sx={{ mt: 2 }}>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    פרטי הקבוצה עודכנו בהצלחה!
                  </Alert>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              {editFormStep === 'form' && (
                <>
                  <Button onClick={handleCloseEditDialog} color="inherit" sx={{ fontSize: '1.1rem' }}>
                    ביטול
                  </Button>
                  <Button 
                    onClick={handleEditFormNextStep} 
                    color="primary" 
                    variant="contained"
                    sx={{ fontSize: '1.1rem' }}
                  >
                    המשך
                  </Button>
                </>
              )}
              
              {editFormStep === 'confirm' && (
                <>
                  <Button 
                    onClick={() => setEditFormStep('form')} 
                    color="inherit"
                    sx={{ fontSize: '1.1rem' }}
                  >
                    חזרה
                  </Button>
                  <Button 
                    onClick={handleEditSubmit} 
                    color="primary" 
                    variant="contained"
                    disabled={editLoading}
                    sx={{ fontSize: '1.1rem' }}
                  >
                    {editLoading ? (
                      <>
                        <CircularProgress size={24} sx={{ mr: 1, color: 'inherit' }} />
                        מעדכן...
                      </>
                    ) : (
                      'שמור שינויים'
                    )}
                  </Button>
                </>
              )}
              
              {editFormStep === 'success' && (
                <Button 
                  onClick={handleCloseEditDialog} 
                  color="primary"
                  sx={{ fontSize: '1.1rem' }}
                >
                  סגור
                </Button>
              )}
            </DialogActions>
          </MobileDialog>

          <MobileDialog
            open={addDialogOpen}
            onClose={(event, reason) => {
              if (reason !== 'backdropClick') {
                handleCloseAddDialog();
              }
            }}
            fullWidth
            maxWidth="xl"
          >
            <DialogTitle sx={{ fontSize: '1.8rem' }}>הוסף לקוחות לקבוצה</DialogTitle>
            <DialogContent sx={{ direction: 'rtl', fontSize: '1.2rem', p: 1 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
                <Autocomplete
                  freeSolo
                  options={availableAutocompleteOptions}
                  getOptionLabel={(option) => {
                    if (typeof option === 'string') return option;
                    if (option.isNoResults) return option.ClientName;
                    return `${option.ClientName} (${option.ClientBusinessID})`;
                  }}
                  getOptionDisabled={(option) => option.isNoResults === true}
                  value={selectedAvailableValue}
                  openOnFocus
                  onInputChange={(event, newValue, reason) => {
                    // Clear previous timer
                    if (availableAutocompleteTimerCleanup.current) {
                      availableAutocompleteTimerCleanup.current();
                    }

                    // Debounce the API call - but DON'T update table search on every keystroke
                    availableAutocompleteTimerCleanup.current = debouncedSetAvailableAutocompleteSearch(newValue || '');

                    // Clear table search if input is emptied (manually deleted all text)
                    if (newValue.trim() === '' && availableTableSearch !== '') {
                      setAvailableTableSearch('');
                      setSelectedAvailableValue(null);
                    }
                  }}
                  onChange={(event, newValue) => {
                    // Ignore dummy "no results" record
                    if (newValue && typeof newValue === 'object' && newValue.isNoResults) {
                      return;
                    }

                    if (typeof newValue === 'string' && newValue.trim() !== '') {
                      // Typed and pressed enter - NOW update table search
                      setAvailableTableSearch(newValue);
                      setSelectedAvailableValue(null);
                    } else if (newValue && typeof newValue === 'object') {
                      // Selected an option - NOW update table search by client business ID
                      setAvailableTableSearch(newValue.ClientBusinessID);
                      setSelectedAvailableValue(newValue);
                    } else {
                      // Cleared or empty string - clear table search and autocomplete search
                      setAvailableTableSearch('');
                      setSelectedAvailableValue(null);
                      setDebouncedAvailableAutocompleteSearch('');
                      // Clear any pending timer
                      if (availableAutocompleteTimerCleanup.current) {
                        availableAutocompleteTimerCleanup.current();
                      }
                    }
                  }}
                  isOptionEqualToValue={(option, value) => option?.ClientBusinessID === value?.ClientBusinessID}
                  filterOptions={(x) => x}
                  loading={isLoadingAvailableClientsAutocomplete}
                  loadingText="טוען..."
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="חיפוש לפי שם או תז/חפ"
                      placeholder="הקלד שם או תז/חפ"
                      size="small"
                      sx={{ width: 300, direction: 'rtl', m: 0, p: 0 }}
                    />
                  )}
                />
                <TextField
                  select
                  label="סינון עזר"
                  size="small"
                  value={addClientFilter}
                  onChange={(e) => setAddClientFilter(e.target.value)}
                  sx={{ width: 200, direction: 'rtl', m: 0, p: 0 }}
                >
                  <MenuItem value="">הכל</MenuItem>
                  {/* Dynamically create filter options based on allowed group types */}
                  {allowedGroupTypes.includes('Flow') && (
                    <MenuItem value="NO_FLOW">ללא קבוצת {getGroupTypeLabel('Flow')}</MenuItem>
                  )}
                  {allowedGroupTypes.includes('Year') && (
                    <MenuItem value="NO_YEAR">ללא קבוצת {getGroupTypeLabel('Year')}</MenuItem>
                  )}
                  {allowedGroupTypes.includes('General') && (
                    <MenuItem value="NO_GENERAL">ללא קבוצות {getGroupTypeLabel('General')}</MenuItem>
                  )}
                  <MenuItem value="NO_GROUP">ללא כל קבוצה</MenuItem>
                </TextField>
              </Box>
              {dialogSelectedClients.length > 0 && (
                <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    label={`נבחרו ${dialogSelectedClients.length} לקוחות`}
                    color="primary"
                    sx={{ fontSize: '1rem' }}
                  />
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setDialogSelectedClients([])}
                    sx={{ fontSize: '0.9rem' }}
                  >
                    נקה בחירה
                  </Button>
                </Box>
              )}
              {isLoadingAvailableClientsPaginated ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <DataGrid
                  rows={availableClientsPaginated || []}
                  columns={addDialogColumns}
                  getRowId={(row) => row.ClientBusinessID}
                  autoHeight
                  loading={isLoadingAvailableClientsPaginated}
                  paginationMode="server"
                  rowCount={availableRowCount}
                  pageSizeOptions={[25, 50, 100]}
                  paginationModel={availablePaginationModel}
                  onPaginationModelChange={setAvailablePaginationModel}
                  checkboxSelection
                  rowSelectionModel={dialogSelectedClients.filter((id) =>
                    visibleAvailableClientIds.has(id)
                  )}
                  onRowSelectionModelChange={(newSelectionModel) => {
                    setDialogSelectedClients((prev) => {
                      // Keep selections from other pages
                      const prevNonVisible = prev.filter((id) => !visibleAvailableClientIds.has(id));
                      // Merge with new selections from current page
                      return Array.from(new Set([...prevNonVisible, ...newSelectionModel]));
                    });
                  }}
                  disableSelectionOnClick
                  localeText={heIL.components.MuiDataGrid.defaultProps.localeText}
                  sx={{
                    '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 'bold', fontSize: '1.2rem', textAlign: 'center' },
                    '.MuiDataGrid-cell': { fontSize: '1.1rem', padding: '4px 8px', textAlign: 'center' },
                  }}
                />
              )}
            </DialogContent>
            <DialogActions>
              <Button
                sx={{ fontSize: '1rem' }}
                onClick={handleCloseAddDialog}
                disabled={isAdding}
              >
                ביטול
              </Button>
              <Button
                sx={{ fontSize: '1rem' }}
                onClick={handleAddClients}
                color="primary"
                disabled={isAdding}
              >
                {isAdding ? <CircularProgress size={24} /> : 'הוסף'}
              </Button>
            </DialogActions>
          </MobileDialog>

          <MobileDialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
            <DialogTitle sx={{ fontSize: '1.8rem' }}>אישור הסרה</DialogTitle>
            <DialogContent sx={{ fontSize: '1.2rem' }}>
              האם אתה בטוח שברצונך להסיר את הלקוח "{selectedClientForRemoval?.ClientName}"?
            </DialogContent>
            <DialogActions>
              <Button sx={{ fontSize: '1rem' }} onClick={() => setConfirmDialogOpen(false)}>
                ביטול
              </Button>
              <Button sx={{ fontSize: '1rem' }} color="error" onClick={handleRemoveClient}>
                הסר
              </Button>
            </DialogActions>
          </MobileDialog>

          <Snackbar
            open={snackbar.open}
            autoHideDuration={2000}
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          >
            <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })} sx={{ width: '100%' }}>
              {snackbar.message}
            </Alert>
          </Snackbar>

          <Divider sx={{ my: 4 }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
             היסטוריה  
          </Typography>
          {/* ===== NEW: Activation History Section as MUI DataGrid (RTL and same styling as reference) ===== */}
          <Card sx={{ borderRadius: 2, boxShadow: 3, mt: 3 }}>
            <CardHeader
              title={
                <Typography sx={{ fontWeight: 'bold', fontSize: '1.3rem' }}>
                  היסטוריית הפעלות
                </Typography>
              }
              sx={{
                backgroundColor: 'primary.main',
                color: 'white',
                py: 1.5,
              }}
            />
            <CardContent>
              {loadingGroupHistory ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box sx={{ width: '100%', overflowX: 'auto' }}>
                  <DataGrid
                    rows={groupHistory || []}
                    columns={historyColumns}
                    getRowId={(row) => row.id}
                    autoHeight
                    pageSizeOptions={[5, 10, 25]}
                    initialState={{
                      pagination: { paginationModel: { pageSize: 5, page: 0 } },
                      sorting: { sortModel: [{ field: 'activated_at', sort: 'desc' }] },
                    }}
                    disableSelectionOnClick
                    localeText={heIL.components.MuiDataGrid.defaultProps.localeText}
                    components={{
                      Toolbar: () => null,
                      NoRowsOverlay: () => (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                          אין היסטוריה להצגה
                        </Box>
                      ),
                    }}
                    sx={{
                      direction: 'rtl',
                      '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 'bold', fontSize: '1.15rem' },
                      '.MuiDataGrid-cell': { fontSize: '1rem', whiteSpace: 'normal', wordBreak: 'break-word' },
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>
          {/* ===== End Activation History Section ===== */}

          {/* 2. ADD THIS DIALOG before the closing </Container> tag */}
          <MobileDialog
            open={permanentDeleteDialogOpen}
            onClose={(event, reason) => {
              if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
              handleCancelPermanentDelete();
            }}
            maxWidth="md" 
            fullWidth
          >
            <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'error.main' }}>
              {permanentDeleteStep === 1 ? 'אזהרה: מחיקה קבועה של קבוצה' : 'הזן סיסמה לאישור'}
            </DialogTitle>
            
            <DialogContent dividers>
              {permanentDeleteError && (
                <Alert ref={permanentDeleteErrorRef} severity="error" sx={{ mb: 2 }}>{permanentDeleteError}</Alert>
              )}
              
              {permanentDeleteStep === 1 ? (
                <Box>
                  <Alert severity="error" sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      פעולה בלתי הפיכה!
                    </Typography>
                  </Alert>
                  
                  <Typography variant="body1" sx={{ mb: 3, fontSize: '1.3rem', fontWeight: 'bold' }}>
                    פעולה זו תבצע את הפעולות הבאות:
                  </Typography>
                  
                  <Box sx={{ mb: 3, p: 2 }}>
                    <Typography variant="body1" sx={{ mb: 2, fontSize: '1.1rem', fontWeight: 'bold' }}>
                      1. מחיקת הקבוצה: <strong>{group?.GroupName}</strong> ({getGroupTypeLabel(group?.GroupType)})
                    </Typography>
                    
                    <Typography variant="body1" sx={{ mb: 2, fontSize: '1.1rem', fontWeight: 'bold' }}>
                      2. מחיקת כל הלקוחות בקבוצה
                    </Typography>
                  </Box>
                  
                  <Typography variant="body1" sx={{ mb: 2, fontSize: '1.2rem' }}>
                    הקבוצה כוללת <strong>{rowCount} לקוחות</strong>. התנהגות לגבי הלקוחות:
                  </Typography>

                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      • לקוחות שחברים רק בקבוצה זו יימחקו לצמיתות עם כל הנתונים שלהם
                    </Typography>
                  
                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                      • לקוחות שחברים גם בקבוצות אחרות לא יימחקו - רק החברות בקבוצה זו תוסר
                    </Typography>
                  
                    <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                      לא ניתן לשחזר לקוחות שנמחקו לצמיתות!
                    </Typography>
                </Box>
              ) : (
                <Box>
                  <Typography sx={{ mb: 2, fontSize: '1.2rem' }}>
                    לאישור המחיקה הקבועה, הזן/י את הסיסמה שלך:
                  </Typography>
                  
                  <TextField
                    label="סיסמה"
                    type="password"
                    fullWidth
                    value={permanentDeletePassword}
                    onChange={(e) => setPermanentDeletePassword(e.target.value)}
                    sx={{ mb: 2, direction: 'ltr' }}
                  />
                </Box>
              )}
            </DialogContent>
            
            <DialogActions>
              <Button onClick={handleCancelPermanentDelete} disabled={permanentDeleteLoading}>
                ביטול
              </Button>
              
              {permanentDeleteStep === 1 ? (
                <Button 
                  onClick={() => setPermanentDeleteStep(2)} 
                  variant="contained" 
                  color="error"
                >
                  המשך למחיקה
                </Button>
              ) : (
                <Button 
                  onClick={handleConfirmPermanentDelete}
                  variant="contained" 
                  color="error"
                  disabled={permanentDeleteLoading || !permanentDeletePassword}
                >
                  {permanentDeleteLoading ? 'מוחק לצמיתות...' : 'מחק לצמיתות'}
                </Button>
              )}
            </DialogActions>
          </MobileDialog>

          {/* ===== Activation Delete Dialog ===== */}
          <MobileDialog
            open={deleteActivationOpen}
            onClose={(event, reason) => {
              if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
              closeDeleteActivation();
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 'bold', color: 'error.main' }}>
              {deleteActivationStep === 'confirm' ? 'אישור מחיקת פעולה' : 'הזן סיסמה לאישור'}
            </DialogTitle>
            <DialogContent dividers>
              {deleteActivationError && (
                <Alert severity="error" sx={{ mb: 2 }}>{deleteActivationError}</Alert>
              )}
              {deleteActivationStep === 'confirm' && deleteActivationTarget && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Alert severity="warning">
                    פעולה זו תמחק את רישום הפעולה ואת כל התנועות שנוצרו על ידה לכל לקוחות הקבוצה.
                    יתרות הלקוחות יחושבו מחדש.
                  </Alert>
                  <Typography>תאריך רישום: {formatDateTime(deleteActivationTarget.activated_at)}</Typography>
                  <Typography>
                    תאריך בפועל:{' '}
                    {deleteActivationTarget.actual_date
                      ? formatDateOnly(deleteActivationTarget.actual_date)
                      : formatDateTime(deleteActivationTarget.activated_at)}
                  </Typography>
                  <Typography>סכום: {deleteActivationTarget.amount}</Typography>
                  <Typography>שדה יעד: {getDebtFieldLabel(deleteActivationTarget.target_field || '')}</Typography>
                  {deleteActivationTarget.note && (
                    <Typography>הערות: {deleteActivationTarget.note}</Typography>
                  )}
                </Box>
              )}
              {deleteActivationStep === 'password' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography>להשלמת המחיקה, הזן/י את הסיסמה שלך:</Typography>
                  <TextField
                    label="סיסמה"
                    type={deleteActivationPwVisible ? 'text' : 'password'}
                    value={deleteActivationPassword}
                    onChange={(e) => setDeleteActivationPassword(e.target.value)}
                    fullWidth
                    sx={{ direction: 'ltr' }}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => setDeleteActivationPwVisible(!deleteActivationPwVisible)}
                          edge="end"
                        >
                          {deleteActivationPwVisible ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      ),
                    }}
                  />
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={closeDeleteActivation} color="inherit" disabled={deleteActivationLoading}>
                ביטול
              </Button>
              {deleteActivationStep === 'confirm' && (
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => {
                    setDeleteActivationError('');
                    setDeleteActivationStep('password');
                  }}
                >
                  המשך למחיקה
                </Button>
              )}
              {deleteActivationStep === 'password' && (
                <>
                  <Button
                    color="inherit"
                    onClick={() => {
                      setDeleteActivationError('');
                      setDeleteActivationStep('confirm');
                    }}
                    disabled={deleteActivationLoading}
                  >
                    חזרה
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={submitDeleteActivation}
                    disabled={deleteActivationLoading || !deleteActivationPassword}
                  >
                    {deleteActivationLoading ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1, color: 'inherit' }} />
                        מוחק...
                      </>
                    ) : (
                      'מחק'
                    )}
                  </Button>
                </>
              )}
            </DialogActions>
          </MobileDialog>

          {/* ===== Activation Edit (Date) Dialog ===== */}
          <MobileDialog
            open={editActivationOpen}
            onClose={(event, reason) => {
              if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
              closeEditActivation();
            }}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 'bold' }}>
              {editActivationStep === 'form'
                ? 'עריכת תאריך הפעולה'
                : editActivationStep === 'confirm'
                ? 'אישור נתונים'
                : 'הזן סיסמה לאישור סופי'}
            </DialogTitle>
            <DialogContent dividers>
              {editActivationError && (
                <Alert severity="error" sx={{ mb: 2 }}>{editActivationError}</Alert>
              )}
              {editActivationStep === 'form' && editActivationTarget && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Alert severity="info">
                    שינוי הערכים יעדכן את כל התנועות שנוצרו על ידי פעולה זו. שינוי תאריך גם יחשב מחדש את יתרות הלקוחות.
                  </Alert>
                  <Typography>תאריך רישום: {formatDateTime(editActivationTarget.activated_at)}</Typography>
                  <Typography>סכום: {editActivationTarget.amount}</Typography>
                  <DatePicker
                    label="תאריך בפועל"
                    value={editActivationDate}
                    onChange={(newValue) => setEditActivationDate(newValue)}
                    format="dd/MM/yyyy"
                    sx={{ direction: 'ltr' }}
                    slotProps={{
                      textField: { fullWidth: true },
                      actionBar: { actions: ['today', 'accept'] },
                    }}
                  />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      הערה מקורית: {editActivationTarget.note || '—'}
                    </Typography>
                    <TextField
                      label="הערה"
                      multiline
                      minRows={2}
                      value={editActivationNote}
                      onChange={(e) => setEditActivationNote(e.target.value)}
                      fullWidth
                    />
                  </Box>
                </Box>
              )}
              {editActivationStep === 'confirm' && editActivationTarget && (() => {
                const originalNote = editActivationTarget.note || '';
                const noteChanged = editActivationNote !== originalNote;
                return (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography sx={{ fontWeight: 'bold' }}>לאשר את העדכון?</Typography>
                    <Typography>
                      תאריך בפועל קודם:{' '}
                      {editActivationTarget.actual_date
                        ? formatDateOnly(editActivationTarget.actual_date)
                        : formatDateTime(editActivationTarget.activated_at)}
                    </Typography>
                    <Typography>
                      תאריך בפועל חדש:{' '}
                      {editActivationDate ? format(editActivationDate, 'dd/MM/yyyy') : '—'}
                    </Typography>
                    {noteChanged ? (
                      <>
                        <Typography>הערה קודמת: {originalNote || '—'}</Typography>
                        <Typography>הערה חדשה: {editActivationNote || '—'}</Typography>
                      </>
                    ) : (
                      <Typography color="text.secondary">ההערה לא תשתנה</Typography>
                    )}
                  </Box>
                );
              })()}
              {editActivationStep === 'password' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Typography>להשלמת העדכון, הזן/י את הסיסמה שלך:</Typography>
                  <TextField
                    label="סיסמה"
                    type={editActivationPwVisible ? 'text' : 'password'}
                    value={editActivationPassword}
                    onChange={(e) => setEditActivationPassword(e.target.value)}
                    fullWidth
                    sx={{ direction: 'ltr' }}
                    InputProps={{
                      endAdornment: (
                        <IconButton
                          onClick={() => setEditActivationPwVisible(!editActivationPwVisible)}
                          edge="end"
                        >
                          {editActivationPwVisible ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      ),
                    }}
                  />
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={closeEditActivation} color="inherit" disabled={editActivationLoading}>
                ביטול
              </Button>
              {editActivationStep === 'form' && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    if (!editActivationDate) {
                      setEditActivationError('יש לבחור תאריך');
                      return;
                    }
                    setEditActivationError('');
                    setEditActivationStep('confirm');
                  }}
                >
                  שלב הבא
                </Button>
              )}
              {editActivationStep === 'confirm' && (
                <>
                  <Button color="inherit" onClick={() => setEditActivationStep('form')}>
                    חזרה
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setEditActivationError('');
                      setEditActivationStep('password');
                    }}
                  >
                    אישור
                  </Button>
                </>
              )}
              {editActivationStep === 'password' && (
                <>
                  <Button
                    color="inherit"
                    onClick={() => {
                      setEditActivationError('');
                      setEditActivationStep('confirm');
                    }}
                    disabled={editActivationLoading}
                  >
                    חזרה
                  </Button>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={submitEditActivation}
                    disabled={editActivationLoading || !editActivationPassword}
                  >
                    {editActivationLoading ? (
                      <>
                        <CircularProgress size={20} sx={{ mr: 1, color: 'inherit' }} />
                        מעדכן...
                      </>
                    ) : (
                      'אישור סופי'
                    )}
                  </Button>
                </>
              )}
            </DialogActions>
          </MobileDialog>
        </Container>
      </div>
    </ThemeProvider>
    </LocalizationProvider>
  );
};

export default GroupDetailPage;